// components/DeliveryAddressUS.tsx

import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import { useTheme } from '../../../hooks/ThemeContext';
import { PostcodeServiceFactory } from '../../../service/PostcodeServiceFactory';
import LocationPicker from './LocationPicker'; // Import the LocationPicker component
import { httpsCallableWithAppId } from '../../../service/CallableWrapper';
import { getFunctions } from 'firebase/functions';
import { FIREBASE_APP } from "../../../Firebase";
import { getRemoteEnv } from '../../../config/remoteEnv';

// Define types for the address and errors
type Address = {
  line1: string;
  line2: string;
  city: string;
  country: string;
  postcode: string;
  latitude?: number;   // NEW
  longitude?: number;  // NEW
  matchedAddress?: string; // NEW - to show what Nominatim returned
} | null;

type Errors = {
  line1?: string;
  city?: string;
  state?: string;
  postcode?: string;
};

type ModalStep = 'form' | 'location';

type NominatimResult = {
  lat: string;
  lon: string;
  display_name: string;
};

type Props = {
  address: Address;
  setAddress: (address: Address) => void;
};

const DeliveryAddressUS: React.FC<Props> = ({ address, setAddress }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<ModalStep>('form');
  const [tempAddress, setTempAddress] = useState<any>({
    line1: '',
    line2: '',
    city: '',
    state: '', 
    postcode: ''
  });
  const [detectedLocation, setDetectedLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [matchedAddress, setMatchedAddress] = useState<string>('');
  const [errors, setErrors] = useState<Errors>({});
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  const postcodeService = PostcodeServiceFactory.getPostcodeService();

  const { theme } = useTheme();

  const validateFields = (): boolean => {
    const newErrors: Errors = {};
    if (!tempAddress?.line1.trim()) newErrors.line1 = 'Street is required';
    if (!tempAddress?.city.trim()) newErrors.city = 'City is required';
    if (!tempAddress?.state.trim()) newErrors.state = 'State is required';
    if (!tempAddress?.postcode.trim()) newErrors.postcode = 'ZIP Code is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Query Nominatim with only line1 and postcode
  const queryNominatim = async (street: string, zipCode: string): Promise<NominatimResult | null> => {
    console.log("QUERY NOMINATIM")
    try {
      const url = `https://nominatim.openstreetmap.org/search?` +
        `street=${encodeURIComponent(street)}&` +
        `postalcode=${encodeURIComponent(zipCode)}&` +
        `format=json&` +
        `limit=1&` +
        `countrycodes=us`;

      const response = await fetch(url, {
        headers: {
          'User-Agent': 'YourAppName/1.0' // Nominatim requires a user agent
        }
      });
      console.log("PAST QUERY")
      if (!response.ok) {
        throw new Error('Nominatim query failed');
      }

      const data = await response.json();
      
      if (data && data.length > 0) {
        return {
          lat: data[0].lat,
          lon: data[0].lon,
          display_name: data[0].display_name
        };
      }
      
      return null;
    } catch (error) {
      const functions = getFunctions(FIREBASE_APP(), getRemoteEnv().region)
      const geocodeService = httpsCallableWithAppId(functions, "geocodeAddress");
      let r = await geocodeService({address: {line1: street, postcode: zipCode}});
      let geocodeData: any = r.data;
      if (geocodeData.errored){
        console.log(r.data);
        console.error('Error querying:', error);
        return null;
      }
      else {
        return {
            lat: geocodeData.latitude,
            lon: geocodeData.longitude,
            display_name: geocodeData.matchedAddress
        }
      }
    }
  };

  const handleFindLocation = async () => {
    if (!validateFields()) return;

    // Validate ZIP code first
    let validLocation = (await postcodeService.validatePostcode(tempAddress.postcode)).isValid;

    if (!validLocation) {
      const newErrors: Errors = {};
      newErrors.postcode = "Invalid ZIP code entered";
      setErrors(newErrors);
      return;
    }

    // Query Nominatim with only line1 and postcode
    setIsLoadingLocation(true);
    setErrors({});

    try {
      const result = await queryNominatim(tempAddress.line1, tempAddress.postcode);
      
      if (result) {
        setDetectedLocation({
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon)
        });
        setMatchedAddress(result.display_name);
        setModalStep('location');
      } else {
        const newErrors: Errors = {};
        newErrors.line1 = "Could not find location for this address. Please check the street address and ZIP code.";
        setErrors(newErrors);
      }
    } catch (error) {
      console.error('Error finding location:', error);
      const newErrors: Errors = {};
      newErrors.line1 = "Error looking up address location. Please try again.";
      setErrors(newErrors);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  const handleLocationChange = (latitude: number, longitude: number) => {
    console.log("setting detected location to: ", latitude, longitude)
    setDetectedLocation({ latitude, longitude });
  };

  const handleConfirmLocation = () => {
    if (!detectedLocation) return;

    // Save address with latitude and longitude
    const finalAddress = {
      ...tempAddress,
      latitude: detectedLocation.latitude,
      longitude: detectedLocation.longitude,
      matchedAddress: matchedAddress
    };

    setAddress(finalAddress);
    localStorage.setItem('deliveryAddress', JSON.stringify(finalAddress));
    localStorage.setItem('userPostcode', tempAddress.postcode);
    
    // Reset modal
    setIsModalOpen(false);
    setModalStep('form');
    setDetectedLocation(null);
    setMatchedAddress('');
  };

  const handleEditClick = () => {
    const editAddress = address ? address : {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postcode: ''
    };
    
    setTempAddress(editAddress);
    setIsModalOpen(true);
    setModalStep('form');
    setErrors({});
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalStep('form');
    setDetectedLocation(null);
    setMatchedAddress('');
    setErrors({});
  };

  const handleBackToForm = () => {
    setModalStep('form');
    setDetectedLocation(null);
    setMatchedAddress('');
  };

  const formatAddress = (): string => {
    const parts = [
      address?.line1,
      address?.line2,
      address?.city,
      address?.country, // This will be the state
      address?.postcode
    ].filter(part => part && part.trim() !== '');
    return parts.join(', ') || 'Add delivery address';
  };

  const modalStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 999900,
      padding: '10px'
    },
    modal: {
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.backgroundColor,
      border: "1px solid " + theme.borderColor,
      padding: '20px',
      borderRadius: '8px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '95vh',
      overflowY: 'auto',
    },
    header: {
      marginBottom: '15px',
      fontSize: '1.2rem',
      fontWeight: 'bold'
    },
    subheader: {
      marginBottom: '12px',
      fontSize: '0.85rem',
      color: '#666'
    },
    input: {
      width: '90%',
      padding: '12px',
      marginBottom: '15px',
      border: '1px solid #ccc',
      borderRadius: '4px'
    },
    error: {
      color: 'red',
      fontSize: '0.8rem',
      marginTop: '-10px',
      marginBottom: '10px'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '15px',
      position: 'sticky',
      bottom: 0,
      padding: '10px 0',
      backgroundColor: theme.backgroundColor
    },
    button: {
      padding: '12px 24px',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem'
    },
    cancelButton: {
      backgroundColor: '#f0f0f0',
      color: '#333'
    },
    saveButton: {
      backgroundColor: theme.payButtonColor,
      color: theme.payButtonTextColor
    },
    matchedAddressBox: {
      backgroundColor: '#f8f9fa',
      padding: '12px',
      borderRadius: '6px',
      marginBottom: '15px',
      border: '1px solid #e0e0e0'
    },
    matchedAddressLabel: {
      fontSize: '0.8rem',
      color: '#666',
      marginBottom: '6px',
      fontWeight: '500'
    },
    matchedAddressText: {
      fontSize: '0.85rem',
      color: '#333',
      lineHeight: '1.4'
    },
    locationPickerContainer: {
      width: '100%',
      height: '350px',
      marginBottom: '15px',
      borderRadius: '8px',
      overflow: 'hidden'
    }
  };
  
  const modalMediaQueryStyles = `
    @media (max-width: 767px) {
      .delivery-address-modal {
        max-width: 95vw !important;
        max-height: 95vh !important;
        padding: 15px !important;
      }
      .delivery-address-modal .location-picker-container {
        height: 300px !important;
      }
    }
    
    @media (min-width: 768px) {
      .delivery-address-modal {
        max-height: 85vh !important;
      }
    }
  `;

  return (
    <div className="w-full px-4 py-2 bg-gray-100 flex items-center justify-between rounded-md">
      <button 
        onClick={handleEditClick}
        className="w-full px-4 py-2 bg-gray-100 flex items-center justify-between rounded-md hover:bg-gray-200 transition-colors duration-200"
        style={{ 
          border: "none", 
          backgroundColor: theme.payButtonColor, 
          color: theme.payButtonTextColor, 
          width: "100%", 
          marginTop: "5px", 
          marginBottom: "5px", 
          height: "40px", 
          cursor: "pointer" 
        }}
      >
        <div className="flex items-center gap-2">
          {!address?.line1 && <span>Enter Delivery Address: </span>}
          {address?.line1 && (
            <>
              <span>Deliver to: </span>
              <span className="font-medium">
                {address.line1.length > 23 ? address.line1.slice(0,20) + "..." : address.line1}
              </span>
            </>
          )}
          &nbsp;&nbsp;
          <Pencil size={12} />
        </div>
      </button>

      {isModalOpen && (
        <>
          <style>{modalMediaQueryStyles}</style>
          <div style={modalStyles.overlay}>
            <div className="delivery-address-modal" style={modalStyles.modal}>
              {modalStep === 'form' ? (
                <div style={{display: "flex", flexDirection: "column"}}>
                  <div style={modalStyles.header}>US Delivery Address</div>
                  
                  <div>
                    <input
                      type="text"
                      value={tempAddress.line2 || ""}
                      onChange={(e) => setTempAddress({ ...tempAddress, line2: e.target.value })}
                      placeholder="Apt / Unit"
                      style={modalStyles.input}
                    />
                  </div>
                  
                  <div>
                    <input
                      type="text"
                      value={tempAddress.line1 || ""}
                      onChange={(e) => setTempAddress({ ...tempAddress, line1: e.target.value })}
                      placeholder="Street Address*"
                      style={{...modalStyles.input, border: `1px solid ${errors.line1 ? 'red' : '#ccc'}`}}
                    />
                    {errors.line1 && <div style={modalStyles.error}>{errors.line1}</div>}
                  </div>

                  <div>
                    <input
                      type="text"
                      value={tempAddress.city || ""}
                      onChange={(e) => setTempAddress({ ...tempAddress, city: e.target.value })}
                      placeholder="City*"
                      style={{...modalStyles.input, border: `1px solid ${errors.city ? 'red' : '#ccc'}`}}
                    />
                    {errors.city && <div style={modalStyles.error}>{errors.city}</div>}
                  </div>

                  <div>
                    <input
                      type="text"
                      value={tempAddress.state || ""}
                      onChange={(e) => setTempAddress({ ...tempAddress, state: e.target.value })}
                      placeholder="State*"
                      style={{...modalStyles.input, border: `1px solid ${errors.state ? 'red' : '#ccc'}`}}
                    />
                    {errors.state && <div style={modalStyles.error}>{errors.state}</div>}
                  </div>

                  <div>
                    <input
                      type="text"
                      value={tempAddress.postcode || ""}
                      onChange={(e) => setTempAddress({ ...tempAddress, postcode: e.target.value })}
                      placeholder="ZIP Code*"
                      style={{...modalStyles.input, border: `1px solid ${errors.postcode ? 'red' : '#ccc'}`}}
                    />
                    {errors.postcode && <div style={modalStyles.error}>{errors.postcode}</div>}
                  </div>

                  <div style={modalStyles.buttonContainer}>
                    <button 
                      onClick={handleModalClose}
                      style={{...modalStyles.button, ...modalStyles.cancelButton}}
                      disabled={isLoadingLocation}
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleFindLocation}
                      style={{...modalStyles.button, ...modalStyles.saveButton}}
                      disabled={isLoadingLocation}
                    >
                      {isLoadingLocation ? 'Finding Location...' : 'Continue to Location'}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{display: "flex", flexDirection: "column"}}>
                  <div style={modalStyles.header}>Confirm Delivery Location</div>
                  <div style={modalStyles.subheader}>
                    <span style={{color: "white", padding: 0}}>Confirm that this is your correct delivery address</span>
                  </div>

                  {/* Matched Address Display */}
                  {matchedAddress && (
                    <div style={modalStyles.matchedAddressBox}>
                      <div style={modalStyles.matchedAddressLabel}>
                        Matched Address:
                      </div>
                      <div style={modalStyles.matchedAddressText}>
                        {matchedAddress}
                      </div>
                    </div>
                  )}

                  {/* Location Picker */}
                  {detectedLocation && (
                    <div style={modalStyles.locationPickerContainer} className="location-picker-container">
                      <LocationPicker
                        initialLatitude={detectedLocation.latitude}
                        initialLongitude={detectedLocation.longitude}
                        onLocationChange={handleLocationChange}
                        address={`${tempAddress.line1}, ${tempAddress.city}, ${tempAddress.state} ${tempAddress.postcode}`}
                      />
                    </div>
                  )}

                  <div style={modalStyles.buttonContainer}>
                    <button 
                      onClick={handleBackToForm}
                      style={{...modalStyles.button, ...modalStyles.cancelButton}}
                    >
                      Back
                    </button>
                    <button 
                      onClick={handleConfirmLocation}
                      style={{...modalStyles.button, ...modalStyles.saveButton}}
                    >
                      Confirm Location
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DeliveryAddressUS;